export type PoiCategoryId = 'shops' | 'education' | 'transport' | 'leisure' | 'services' | 'health';

export interface PoiCategoryConfig {
  id: PoiCategoryId;
  label: string;
  icon: string;
  color: string;
  bgColor: string;
  textColor: string;
  borderColor: string;
  badgeBg: string;
}

export interface OsmPoi {
  id: string;
  name: string;
  category: PoiCategoryId;
  lat: number;
  lng: number;
  distanceMeters?: number;
  details?: string;
}

export const POI_CATEGORIES: PoiCategoryConfig[] = [
  {
    id: 'shops',
    label: 'Commerces',
    icon: 'storefront',
    color: '#0284c7',
    bgColor: 'bg-sky-2 dark:bg-sky-3/20',
    textColor: 'text-sky-11 dark:text-sky-9',
    borderColor: 'border-sky-6 dark:border-sky-5/40',
    badgeBg: 'bg-sky-9',
  },
  {
    id: 'education',
    label: 'Éducation',
    icon: 'school',
    color: '#d97706',
    bgColor: 'bg-amber-2 dark:bg-amber-3/20',
    textColor: 'text-amber-11 dark:text-amber-9',
    borderColor: 'border-amber-6 dark:border-amber-5/40',
    badgeBg: 'bg-amber-9',
  },
  {
    id: 'transport',
    label: 'Transports',
    icon: 'directions_bus',
    color: '#16a34a',
    bgColor: 'bg-green-2 dark:bg-green-3/20',
    textColor: 'text-green-11 dark:text-green-9',
    borderColor: 'border-green-6 dark:border-green-5/40',
    badgeBg: 'bg-green-9',
  },
  {
    id: 'leisure',
    label: 'Loisirs & Espaces verts',
    icon: 'park',
    color: '#9333ea',
    bgColor: 'bg-purple-2 dark:bg-purple-3/20',
    textColor: 'text-purple-11 dark:text-purple-9',
    borderColor: 'border-purple-6 dark:border-purple-5/40',
    badgeBg: 'bg-purple-9',
  },
  {
    id: 'services',
    label: 'Services publics',
    icon: 'account_balance',
    color: '#4f46e5',
    bgColor: 'bg-indigo-2 dark:bg-indigo-3/20',
    textColor: 'text-indigo-11 dark:text-indigo-9',
    borderColor: 'border-indigo-6 dark:border-indigo-5/40',
    badgeBg: 'bg-indigo-9',
  },
  {
    id: 'health',
    label: 'Santé',
    icon: 'local_hospital',
    color: '#e11d48',
    bgColor: 'bg-red-2 dark:bg-red-3/20',
    textColor: 'text-red-11 dark:text-red-9',
    borderColor: 'border-red-6 dark:border-red-5/40',
    badgeBg: 'bg-red-9',
  },
];

/**
 * Calculate geodesic distance between two points in meters using Haversine formula.
 */
export function calculateDistanceMeters(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const R = 6371e3;
  const radLat1 = (lat1 * Math.PI) / 180;
  const radLat2 = (lat2 * Math.PI) / 180;
  const deltaLat = ((lat2 - lat1) * Math.PI) / 180;
  const deltaLon = ((lon2 - lon1) * Math.PI) / 180;

  const a =
    Math.sin(deltaLat / 2) * Math.sin(deltaLat / 2) +
    Math.cos(radLat1) * Math.cos(radLat2) * Math.sin(deltaLon / 2) * Math.sin(deltaLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

  return Math.round(R * c);
}

/**
 * Map raw OSM tags to structured category and clean label.
 */
export function getPoiFallbackName(tags: Record<string, string>): {
  name: string;
  category: PoiCategoryId;
  details?: string;
} | null {
  // 1. Education (Schools, Collèges, Lycées, Universités, Crèches)
  if (
    (tags['amenity'] &&
      [
        'school',
        'kindergarten',
        'college',
        'university',
        'childcare',
        'music_school',
        'dancing_school',
      ].includes(tags['amenity'])) ||
    tags['building'] === 'school' ||
    (tags['name'] &&
      /\b(école|ecole|collège|college|lycée|lycee|crèche|creche|maternelle|élémentaire|elementaire|université|universite|institut|conservatoire|campus|scolaire)\b/i.test(
        tags['name'],
      ))
  ) {
    const name = tags['name'] || tags['brand'];
    if (!name || /^bâtiment\s+[a-z0-9]+/i.test(name.trim()) || /^salle\s+polyvalente/i.test(name.trim())) {
      return null;
    }

    let detail = 'Établissement scolaire';
    const lower = name.toLowerCase();
    if (lower.includes('collège') || lower.includes('college')) {
      detail = 'Collège';
    } else if (lower.includes('lycée') || lower.includes('lycee')) {
      detail = 'Lycée';
    } else if (
      lower.includes('maternelle') ||
      tags['amenity'] === 'kindergarten' ||
      tags['amenity'] === 'childcare'
    ) {
      detail = 'École maternelle / Crèche';
    } else if (
      lower.includes('université') ||
      lower.includes('universite') ||
      lower.includes('campus') ||
      lower.includes('institut') ||
      tags['amenity'] === 'university'
    ) {
      detail = 'Enseignement supérieur';
    } else if (lower.includes('élémentaire') || lower.includes('primaire') || lower.includes('scolaire')) {
      detail = 'École élémentaire / primaire';
    }

    return {
      name: name.trim(),
      category: 'education',
      details: detail,
    };
  }

  // 2. Health & Medical
  if (
    (tags['amenity'] && ['pharmacy', 'doctors', 'hospital', 'clinic', 'dentist'].includes(tags['amenity'])) ||
    (tags['healthcare'] &&
      ['pharmacy', 'doctor', 'hospital', 'clinic', 'dentist', 'centre'].includes(tags['healthcare']))
  ) {
    const healthNames: Record<string, string> = {
      pharmacy: 'Pharmacie',
      doctors: 'Cabinet médical / Médecin',
      doctor: 'Médecin',
      hospital: 'Centre hospitalier',
      clinic: 'Clinique / Centre de soins',
      dentist: 'Cabinet dentaire',
    };
    const key = tags['amenity'] || tags['healthcare'] || 'pharmacy';
    const name = tags['name'] || healthNames[key] || 'Santé';
    if (!name) return null;
    return {
      name: name.trim(),
      category: 'health',
      details: healthNames[key] || 'Santé',
    };
  }

  // 3. Public Services & Administration
  if (
    (tags['amenity'] &&
      ['post_office', 'townhall', 'bank', 'police', 'library', 'courthouse', 'public_building'].includes(
        tags['amenity'],
      )) ||
    tags['office'] === 'government'
  ) {
    const servNames: Record<string, string> = {
      post_office: 'Bureau de poste',
      townhall: 'Mairie',
      bank: 'Banque',
      police: 'Commissariat / Gendarmerie',
      library: 'Médiathèque / Bibliothèque',
      courthouse: 'Palais de justice / Tribunal',
      public_building: 'Service public',
      government: 'Administration publique (CAF, CPAM, etc.)',
    };
    const key = tags['amenity'] || (tags['office'] === 'government' ? 'government' : 'public_building');
    const name = tags['name'] || servNames[key];
    if (!name) return null;
    return {
      name: name.trim(),
      category: 'services',
      details: servNames[key] || 'Service public',
    };
  }

  // 4. Shops & Commerce
  if (tags['shop']) {
    const shopNames: Record<string, string> = {
      bakery: 'Boulangerie',
      supermarket: 'Supermarché',
      convenience: 'Épicerie',
      butcher: 'Boucherie',
      greengrocer: 'Primeur',
      clothes: 'Boutique de vêtements',
      hairdresser: 'Salon de coiffure',
      books: 'Librairie',
      mall: 'Centre commercial',
      newsagent: 'Presse / Tabac',
      florist: 'Fleuriste',
    };
    const name = tags['name'] || tags['brand'] || shopNames[tags['shop']];
    if (!name) return null;
    return {
      name: name.trim(),
      category: 'shops',
      details: tags['shop'] ? `Commerce (${shopNames[tags['shop']] || tags['shop']})` : undefined,
    };
  }

  // 5. Transport
  if (
    tags['highway'] === 'bus_stop' ||
    (tags['railway'] && ['station', 'tram_stop', 'subway_entrance'].includes(tags['railway'])) ||
    (tags['amenity'] && ['bus_station', 'ferry_terminal'].includes(tags['amenity']))
  ) {
    const transNames: Record<string, string> = {
      bus_stop: 'Arrêt de bus',
      station: 'Gare ferroviaire',
      tram_stop: 'Station de tramway',
      subway_entrance: 'Bouche de métro',
    };
    const key = tags['highway'] === 'bus_stop' ? 'bus_stop' : tags['railway'] || tags['amenity'] || '';
    const name = tags['name'] || transNames[key];
    if (!name) return null;
    return {
      name: name.trim(),
      category: 'transport',
    };
  }

  // 6. Leisure & Sport
  if (
    (tags['leisure'] &&
      ['park', 'sports_centre', 'pitch', 'fitness_centre', 'playground', 'garden', 'swimming_pool'].includes(
        tags['leisure'],
      )) ||
    (tags['amenity'] && ['cinema', 'theatre'].includes(tags['amenity']))
  ) {
    const leisureNames: Record<string, string> = {
      park: 'Parc public',
      garden: 'Jardin',
      sports_centre: 'Complexe sportif',
      fitness_centre: 'Salle de sport',
      swimming_pool: 'Piscine municipale',
      playground: 'Aire de jeux',
      cinema: 'Cinéma',
      theatre: 'Théâtre',
    };
    const key = tags['leisure'] || tags['amenity'] || '';
    const name = tags['name'] || leisureNames[key];
    if (!name) return null;
    return {
      name: name.trim(),
      category: 'leisure',
    };
  }

  return null;
}

const POI_CACHE = new Map<string, OsmPoi[]>();
const OVERPASS_ENDPOINTS = [
  'https://overpass-api.de/api/interpreter',
  'https://overpass.kumi.systems/api/interpreter',
];

/**
 * Fetch real Overpass API POIs around latitude/longitude (with in-memory & session caching).
 */
export async function fetchOsmPois(lat: number, lng: number, radiusMeters = 1000): Promise<OsmPoi[]> {
  const cacheKey = `etxe_poi_${lat.toFixed(4)}_${lng.toFixed(4)}_${radiusMeters}`;

  const cached = POI_CACHE.get(cacheKey);
  if (cached) {
    return cached;
  }

  try {
    const sessionCached = typeof window !== 'undefined' ? sessionStorage.getItem(cacheKey) : null;
    if (sessionCached) {
      const parsed = JSON.parse(sessionCached) as OsmPoi[];
      // Ignore old fake fallbacks
      const isFake = parsed.some(
        (p) =>
          p.id?.startsWith('fallback-') ||
          p.id?.startsWith('backend-fallback-') ||
          p.id?.startsWith('dev-fallback-'),
      );
      if (Array.isArray(parsed) && parsed.length > 0 && !isFake) {
        POI_CACHE.set(cacheKey, parsed);
        return parsed;
      }
    }
  } catch {
    // Ignore storage issues
  }

  const query = `
    [out:json][timeout:20];
    (
      nwr["amenity"~"school|kindergarten|college|university|childcare"](around:${radiusMeters},${lat},${lng});
      nwr["building"="school"](around:${radiusMeters},${lat},${lng});
      nwr["shop"](around:${radiusMeters},${lat},${lng});
      nwr["amenity"~"pharmacy|doctors|hospital|clinic|dentist"](around:${radiusMeters},${lat},${lng});
      nwr["healthcare"~"pharmacy|doctor|hospital|clinic|dentist"](around:${radiusMeters},${lat},${lng});
      nwr["amenity"~"post_office|townhall|bank|police|library|courthouse|public_building"](around:${radiusMeters},${lat},${lng});
      nwr["office"="government"](around:${radiusMeters},${lat},${lng});
      nwr["highway"="bus_stop"](around:${radiusMeters},${lat},${lng});
      nwr["railway"~"station|tram_stop|subway_entrance"](around:${radiusMeters},${lat},${lng});
      nwr["leisure"~"park|sports_centre|pitch|fitness_centre|playground|swimming_pool|garden"](around:${radiusMeters},${lat},${lng});
    );
    out center;
  `.trim();

  for (const endpoint of OVERPASS_ENDPOINTS) {
    try {
      const bodyParams = new URLSearchParams({ data: query }).toString();
      const response = await fetch(endpoint, {
        method: 'POST',
        body: bodyParams,
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
          Accept: 'application/json',
        },
      });

      if (!response.ok) {
        continue;
      }

      const data = await response.json();
      const elements: Array<{
        id: number;
        lat?: number;
        lon?: number;
        center?: { lat: number; lon: number };
        tags?: Record<string, string>;
      }> = data?.elements || [];

      const poisByCategory: Record<PoiCategoryId, OsmPoi[]> = {
        education: [],
        health: [],
        services: [],
        transport: [],
        leisure: [],
        shops: [],
      };
      const seenNames = new Set<string>();

      for (const el of elements) {
        const elLat = el.lat ?? el.center?.lat;
        const elLon = el.lon ?? el.center?.lon;
        if (!elLat || !elLon || !el.tags) continue;

        const parsed = getPoiFallbackName(el.tags);
        if (!parsed) continue;

        const dedupeKey = `${parsed.category}-${parsed.name.toLowerCase().trim()}`;
        if (seenNames.has(dedupeKey)) continue;
        seenNames.add(dedupeKey);

        const dist = calculateDistanceMeters(lat, lng, elLat, elLon);
        poisByCategory[parsed.category]?.push({
          id: `osm-${el.id}`,
          name: parsed.name,
          category: parsed.category,
          lat: elLat,
          lng: elLon,
          distanceMeters: dist,
          details: parsed.details,
        });
      }

      const cappedPois: OsmPoi[] = [];
      (Object.keys(poisByCategory) as PoiCategoryId[]).forEach((cat) => {
        poisByCategory[cat].sort((a, b) => (a.distanceMeters || 0) - (b.distanceMeters || 0));
        cappedPois.push(...poisByCategory[cat].slice(0, 20));
      });

      // Global sort ascending by distance
      cappedPois.sort((a, b) => (a.distanceMeters || 0) - (b.distanceMeters || 0));

      if (cappedPois.length > 0) {
        POI_CACHE.set(cacheKey, cappedPois);
        try {
          if (typeof window !== 'undefined') {
            sessionStorage.setItem(cacheKey, JSON.stringify(cappedPois));
          }
        } catch {
          // Ignore quota
        }
        return cappedPois;
      }
    } catch {
      // Try next mirror
    }
  }

  return [];
}
